import React from 'react';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import { HasFormValue } from 'app/common/input/HasFormValue';
import Utils from 'app/common/util/Utils';

export interface SelectEnumProps<T>
    extends HasFormValue<T | T[]>,
        Omit<SelectProps<string | string[]>, 'value' | 'onChange'> {
    enumType: any;
    excludeValues?: T[];
    displayName?(item: T): string;
    displayNameMap?: Map<T, string>;
    canBeEmpty?: boolean;
    multiple?: true;
}

interface SelectEnumState {
    value: string | string[];
}

/**
  Handy component that renders a Select based on Enum values.
  To allow select multiple enum values just pass on the multiple property.
 */
export default class SelectEnum<T> extends React.PureComponent<SelectEnumProps<T>, SelectEnumState> {
    constructor(props: SelectEnumProps<T>) {
        super(props);
        this.state = {
            value: this.props.multiple
                ? (this.props.value as T[])?.map(v => this.props.enumType[v])
                : this.props.enumType[props.value],
        };
    }

    componentDidUpdate(prevProps, prevState): void {
        if (this.props.multiple) {
            const nextValue: string[] = (this.props.value as T[])?.map(ev => ev.toString());
            const currentValue: string[] = this.state.value as string[];
            if (Utils.isNotEquals(nextValue, currentValue)) {
                this.setState({ value: nextValue });
            }
        } else {
            const nextValue: string = this.props.enumType[this.props.value];
            if (nextValue !== this.state.value) {
                this.setState({ value: nextValue });
            }
        }
    }

    handleChange = (enumKey: string) => {
        this.setState({ value: enumKey });
        if (this.props.onChange) {
            this.props.onChange(this.props.enumType[enumKey]);
        }
    };

    handleChangeMultiple = (enumKeys: string[]) => {
        this.setState({ value: enumKeys });
        if (this.props.onChange) {
            const enumValues = enumKeys?.map(v => this.props.enumType[v]);
            this.props.onChange(enumValues);
        }
    };

    displayName = (enumKey: string) => {
        if (this.props.displayName) return this.props.displayName(this.props.enumType[enumKey]);
        else if (this.props.displayNameMap) return this.props.displayNameMap.get(this.props.enumType[enumKey]);
        return enumKey;
    };

    render() {
        const {
            enumType,
            displayName,
            displayNameMap,
            value,
            onChange,
            canBeEmpty,
            multiple,
            excludeValues,
            ...other
        } = this.props;
        return (
            <Select
                mode={multiple ? 'multiple' : undefined}
                value={this.state.value}
                onChange={multiple ? this.handleChangeMultiple : this.handleChange}
                {...other}>
                {Object.keys(this.props.enumType)
                    .filter(
                        value =>
                            isNaN(Number(value)) &&
                            !this.props.excludeValues?.includes(this.props.enumType[value]) === true,
                    )
                    .map((value: string) => {
                        return (
                            <Select.Option key={value} value={value}>
                                {this.displayName(value)}
                            </Select.Option>
                        );
                    })}
                {canBeEmpty ? (
                    <Select.Option key="" value={undefined}>
                        {''}
                    </Select.Option>
                ) : undefined}
            </Select>
        );
    }
}

/**
  Example of usage below ==> 
*/
enum MyEnum {
    A,
    B,
}

const MyEnumDisplayMap = new Map<MyEnum, string>([
    [MyEnum.A, 'AAA'],
    [MyEnum.B, 'BBB'],
]);

interface SelectEnumExampleState {
    value: MyEnum;
}

export class SelectEnumExample extends React.PureComponent<{}, SelectEnumExampleState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            value: MyEnum.A,
        };
    }
    myEnumDisplayName = (item: MyEnum) => {
        switch (item) {
            case MyEnum.A:
                return 'AAA';
            case MyEnum.B:
                return 'BBB';
        }
    };
    handleChange = (value: MyEnum) => {
        this.setState({ value: value });
    };
    render() {
        return (
            <SelectEnum
                value={this.state.value}
                enumType={MyEnum}
                displayName={this.myEnumDisplayName} /** If provided, displayName function is used */
                displayNameMap={
                    MyEnumDisplayMap
                } /** Else, displayName map is used - otherwise, the natural key is used*/
                onChange={this.handleChange}
            />
        );
    }
}
